import { Modal, ModalProps } from '@elements/modal'
import { LoginForm, LoginFormProps } from './login-form'
import { useCallback, useEffect, useState } from 'react'
import { SignupForm } from './signup-form'
import { useSession } from '@hooks/session'
import { ForgotPasswordForm } from './forgot-password-form'
import { H2 } from '@elements/headings'

export interface LoginModalProps extends ModalProps {
  afterLogin?: LoginFormProps['afterLogin']
}

const modalTitle = {
  login: 'Login with Tablelist',
  forgotPassword: 'Reset Your Password',
  signup: 'Signup with Tablelist',
}

export function LoginModal({ afterLogin, ...props }: LoginModalProps) {
  const session = useSession()
  const [mode, setMode] = useState<'login' | 'signup' | 'forgotPassword'>('login')

  const beginSignup = useCallback(() => setMode('signup'), [])
  const beginLogin = useCallback(() => setMode('login'), [])
  const beginForgotPassword = useCallback(() => setMode('forgotPassword'), [])

  const handleAfterLogin = useCallback(async () => {
    await afterLogin?.()
    props.close()
  }, [afterLogin, props])

  useEffect(() => {
    session.clearAuthError()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, mode])

  useEffect(() => {
    if (!props.isOpen) {
      setTimeout(() => {
        setMode('login')
      }, 150)
    }
  }, [props.isOpen])

  return (
    <Modal {...props} header={<H2>{modalTitle[mode]}</H2>} fit className="p-3 max-w-md">
      {mode === 'login' && (
        <>
          <LoginForm afterLogin={handleAfterLogin} onForgotPassword={beginForgotPassword} />
          <p>
            Don&apos;t have an account?{' '}
            <span className="link" onClick={beginSignup}>
              Sign up
            </span>{' '}
            with Tablelist
          </p>
        </>
      )}
      {mode === 'signup' && (
        <>
          <SignupForm afterSignup={handleAfterLogin} />
          <p>
            Already have an account?{' '}
            <span className="link" onClick={beginLogin}>
              Login
            </span>{' '}
            with Tablelist
          </p>
        </>
      )}
      {mode === 'forgotPassword' && (
        <>
          <ForgotPasswordForm />
          <p>
            <span className="link" onClick={beginLogin}>
              Back to login
            </span>
          </p>
        </>
      )}
    </Modal>
  )
}
